import { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import axios from 'axios';
import { API_URL } from '../config/api';
import { Delivery, Hub, DEFAULT_HUB, TrackedParcel, TrackStatus } from './DeliveryMap';
import { findCity, findCountry, findVoivodeshipPlace, countryName, regionOf, distanceKm, GeoPlace } from './europeGeo';
import { courierOfOrder, courierOfTitle, CourierKey } from '../ui/couriers';
import { isAgentOnline } from './agentStatus';

export type DashboardStats = {
  pendingOrders?: number;
  packedToday?: number;
  labelsToday?: number;
  /** Sekundy (v2 API) albo gotowy tekst (starsze /statistics) — patrz fmtDuration w DashboardPage. */
  avgPackingTime?: number | string;
};

export type ActiveStation = {
  id: string | number;
  station_id: string;
  name: string;
  location?: string;
  is_active?: boolean;
  /** Heartbeat aplikacji stacji i wyliczona na jego podstawie „podłączoność" (próg 5 min w API). */
  last_seen_at?: string | null;
  online?: boolean;
  active_session?: {
    user_name?: string;
    order_name?: string;
    items_packed?: number;
    total_items?: number;
  } | null;
};

export type ActiveSession = {
  id: string;
  order_name: string;
  customer_name?: string;
  station_name?: string;
  user_name?: string;
  items_packed: number;
  total_items: number;
  started_at: string;
  status?: string;
};

export type HistoryRow = {
  id: string;
  order_id: string | number;
  order_name: string;
  customer_name?: string;
  station_name?: string;
  user_name?: string;
  started_at: string;
  completed_at: string;
  total_items?: number;
  packages_count?: number;
};

export type AdminOrder = {
  id: number;
  name: string;
  created_at: string;
  customer?: { first_name?: string; last_name?: string };
  shipping_address?: {
    city?: string;
    province?: string;
    province_code?: string;
    country?: string;
    country_code?: string;
    zip?: string;
  };
  total_price?: string;
  currency?: string;
  line_items?: { quantity: number }[];
  note_attributes?: { name: string; value: string }[];
  customAttributes?: { key: string; value: string }[];
  tags?: string;
  shipping_method?: string;
  delivery_info?: { carrier?: string; type?: string; cod?: boolean };
  // Kurier rozpoznany przez UCS (z pakietu kolejki pakowania) — zrodlo prawdy dla
  // odznaki kuriera na liscie/mapie (courierOfOrder). Bez tego courierOf spadalo na 'inpost'.
  carrier?: { pluginId?: string; serviceId?: string; qualifier?: string; title?: string | null } | null;
  packingStatus?: string;
  // Numer śledzenia (z UCS po fulfillmentcie / z fulfillmentu Shopify) — zasila
  // realny tracking UCS na mapie. Brak => paczka jeszcze nienadana.
  trackingNumber?: string;
  trackingCompany?: string;
  /**
   * Powód rozpoznania z UCS: `identified` | `no_shipping_line` | `unknown_service_code`.
   * Wszystko poza `identified` znaczy, że UCS nie wystawi etykiety — paczki nie wolno
   * pakować, dopóki administrator nie skonwertuje jej w zakładce Parser panelu UCS.
   */
  reason?: string;
};

/* Pełna odpowiedź UCS POST /v1/shipments/identify (docs/api_ucs.json:
   IdentifyResponse) — kurier rozpoznany z zamówienia, znormalizowane zamówienie
   Shopify (pozycje, adresy, linie wysyłkowe), stan fulfillmentu oraz gotowy
   shipmentRequest. Dry-run: nic nie generuje, nic nie kosztuje. */
export type UcsAddress = {
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  company?: string | null;
  address1?: string;
  address2?: string | null;
  city?: string;
  province?: string | null;
  zip?: string;
  country_code?: string;
  phone?: string | null;
};

export type UcsOrderLineItem = {
  id?: number;
  title?: string;
  quantity?: number;
  grams?: number | null;
  sku?: string | null;
  variant_id?: number | null;
  product_id?: number | null;
  price?: string | null;
};

export type UcsShippingLine = {
  id?: number;
  code?: string | null;
  title?: string;
  source?: string | null;
  price?: string | null;
  carrier_identifier?: string | null;
};

export type UcsFulfillableLineItem = {
  fulfillmentOrderLineItemId: string;
  fulfillmentOrderId: string;
  orderLineItemId: number;
  sku?: string | null;
  remainingQuantity: number;
  totalQuantity: number;
};

export type UcsExistingFulfillment = {
  id: string;
  status: string;
  createdAt: string;
  trackingNumbers: string[];
  trackingCompany?: string | null;
  trackingUrls: string[];
};

// Gabaryt kuriera (ParcelSize) — katalog wybierany przez packing system. Kwoty z UCS
// zawsze w całkowitych jednostkach minor (grosze/centy) + 3-literowa waluta.
export type ParcelSize = {
  id: string;
  name: string;
  description?: string;
  maxWeight?: { value: number; unit: string };
  maxDimensions?: { length: number; width: number; height: number; unit: string };
  minDimensions?: { length: number; width: number; height: number; unit: string };
  price?: { amount: number; currency: string };
  requiresDimensions?: boolean;
};

// Karton operatora (PackFlow package_types) wzbogacony przez backend o gabaryt kuriera, pod
// który podpada (+ cena). To operator wybiera karton; backend rozstrzyga gabaryt. `gabaryt:null`
// = karton większy niż każdy gabaryt (wysyłka pójdzie na same wymiary). Dla paczkomatu gabaryt
// → template, dla kuriera lecą realne wymiary kartonu — dzięki temu nie ma błędu „dimensions".
export type ClassifiedCarton = {
  id: number;
  name: string;
  length: number;
  width: number;
  height: number;
  weight?: number | null;
  // Kategorie pakowania UCS, do których karton jest przypisany (puste = wszystkie). Picker
  // pokazuje tylko kartony pasujące do kategorii paczki — nie każdy karton pasuje do każdego kuriera.
  categoryIds?: string[];
  gabaryt: { id: string; name: string; price?: { amount: number; currency: string } | null } | null;
};

export type UcsIdentify = {
  identified?: { pluginId: string; serviceId: string; qualifier?: string };
  // Katalog gabarytów rozpoznanego kuriera (od POST /v1/shipments/identify). Wybór
  // przez parcels[].packageType (i/lub jawne dimensions) przy POST /v1/shipments.
  parcelSizes?: ParcelSize[];
  // Kartony operatora połączone z gabarytami powyżej (dokłada backend /api/ucs/identify).
  cartons?: ClassifiedCarton[];
  shipmentRequest?: {
    rateId?: string;
    carrierId?: string;
    serviceId?: string;
    reference?: string;
    customerNote?: string;
    labelFormat?: string;
    idempotencyKey?: string;
    shipFrom?: Record<string, any>;
    shipTo?: Record<string, any>;
    parcels?: { weight?: { value?: number; unit?: string }; dimensions?: any; packageType?: string; declaredValue?: any; description?: string; reference?: string }[];
    metadata?: Record<string, any>;
  };
  order?: {
    id: number;
    name: string;
    email?: string | null;
    currency: string;
    total_weight?: number | null;
    shipping_lines?: UcsShippingLine[];
    shipping_address?: UcsAddress | null;
    billing_address?: UcsAddress | null;
    line_items?: UcsOrderLineItem[];
  };
  fulfillment?: {
    available: boolean;
    fulfillmentStatus?: string | null;
    lineItems?: UcsFulfillableLineItem[];
    existingFulfillments?: UcsExistingFulfillment[];
    reason?: string;
    message?: string;
  };
  // Sugestie prezentów dla tego zamówienia (ADR-0007). Opt-in: gdy darowizny
  // wyłączone, UCS zwraca { enabled:false }. labelNote -> etykieta prezentu.
  // Budżet klienta i koszt produkcji liczone są po stronie UCS i NIE wracają przez
  // API — operator po prostu wybiera produkt.
  gifts?: {
    enabled: boolean;
    reason?: string;
    suggestions: Array<{
      variantId: string | null;
      sku: string | null;
      title: string;
    }>;
    labelNote: string;
  };
};

/* Pobiera pełne dane zamówienia z UCS (kurier + pozycje + adresy + fulfillment).
   Referencja: shopifyOrderId gdy znamy numeryczny id Shopify, inaczej orderName
   ("#1006"). retry:false — przy braku konfiguracji/scope'a UCS pokazujemy błąd
   zamiast zawieszać szufladę. */
export function useUcsIdentify(order: AdminOrder | null) {
  return useQuery<UcsIdentify>({
    queryKey: ['ucs-identify', order?.id ?? order?.name],
    enabled: !!order,
    queryFn: async () => {
      const ref = order?.id ? { shopifyOrderId: order.id } : { orderName: order?.name };
      return (await axios.post(`${API_URL}/api/ucs/identify`, ref)).data;
    },
    retry: false,
    staleTime: 60000,
  });
}

/* Szczegóły zamówienia z UCS dla szuflady (GET /api/ucs/order/:ref → /v1/orders/:ref).
   Czysty odczyt — w przeciwieństwie do identify NIE zwraca 422 dla zamówień „nie do wysyłki"
   (brak linii wysyłkowej / paczek), więc szuflada zawsze się wyrenderuje. Kształt jest zgodny
   z UcsIdentify (po prostu bez shipmentRequest/parcelSizes/cartons). */
export function useUcsOrder(order: AdminOrder | null) {
  return useQuery<UcsIdentify>({
    queryKey: ['ucs-order', order?.id ?? order?.name],
    enabled: !!order,
    queryFn: async () => {
      const ref = order?.id ?? order?.name;
      return (await axios.get(`${API_URL}/api/ucs/order/${encodeURIComponent(String(ref))}`)).data;
    },
    retry: false,
    staleTime: 60000,
  });
}

/* Carrier-agnostyczny katalog gabarytów. v2/api NIE wystawia /api/ucs/carriers/:id/
   parcel-sizes (UCS-only — gabaryty per zamówienie pochodzą z /api/ucs/identify, patrz
   useUcsIdentify). Zostawiamy zgodną sygnaturę, ale hook jest wyłączony (enabled:false),
   więc nigdy nie woła nieistniejącego endpointu — żaden konsument nie dostanie 404. */
export function useParcelSizes(pluginId: string | null | undefined) {
  return useQuery<{ pluginId: string; parcelSizes: ParcelSize[] }>({
    queryKey: ['ucs-parcel-sizes', pluginId],
    enabled: false,
    queryFn: async () => ({ pluginId: pluginId || '', parcelSizes: [] }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDashboardStats() {
  return useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => (await axios.get(`${API_URL}/api/orders/stats/dashboard`)).data,
    refetchInterval: 15000,
  });
}

export function useActiveStations() {
  return useQuery<ActiveStation[]>({
    queryKey: ['active-stations'],
    queryFn: async () => (await axios.get(`${API_URL}/api/packing/stations/active`)).data,
    refetchInterval: 10000,
  });
}

export function useActiveSessions() {
  return useQuery<ActiveSession[]>({
    queryKey: ['active-packing-sessions'],
    queryFn: async () => (await axios.get(`${API_URL}/api/packing/sessions/active`)).data,
    refetchInterval: 5000,
  });
}

export function usePackingHistory(search?: string) {
  return useQuery<HistoryRow[]>({
    queryKey: ['packing-history', search || ''],
    queryFn: async () => {
      const params = search ? { search } : {};
      return (await axios.get(`${API_URL}/api/packing/history`, { params })).data;
    },
    refetchInterval: 30000,
  });
}

/* UCS jako źródło prawdy zamówień (pełna integracja). Mapujemy PackingPackage
   z api_ucs.json na AdminOrder, którego używa lista zamówień, mapa dostaw i rankingi. */
function splitName(n?: string | null): { first?: string; last?: string } {
  const s = (n || '').trim();
  if (!s) return {};
  const parts = s.split(/\s+/);
  return { first: parts[0], last: parts.slice(1).join(' ') || undefined };
}

// Wyciąga numer śledzenia z różnych kształtów payloadu UCS (zależnie od wersji
// API/deploymentu): pole płaskie, obiekt tracking, tablica z fulfillmentu.
function extractTracking(p: any): string | undefined {
  return (
    p?.trackingNumber
    || p?.tracking?.number
    || (Array.isArray(p?.trackingNumbers) ? p.trackingNumbers[0] : undefined)
    || (Array.isArray(p?.fulfillment?.trackingNumbers) ? p.fulfillment.trackingNumbers[0] : undefined)
    || undefined
  );
}

function mapUcsPackageToOrder(p: any): AdminOrder {
  const nm = splitName(p?.customer?.name);
  const tracking = extractTracking(p);
  return {
    id: p?.shopifyOrderId,
    name: p?.orderName,
    created_at: p?.createdAt,
    customer: { first_name: nm.first, last_name: nm.last },
    shipping_address: {
      city: p?.customer?.city || undefined,
      zip: p?.customer?.postalCode || undefined,
      country_code: p?.customer?.countryCode || undefined,
    },
    line_items: Array.isArray(p?.items) ? p.items.map((i: any) => ({ quantity: i?.quantity || 0 })) : [],
    // deliveryDate -> note_attributes 'Delivery Date' (czyta formatDeliverySlot/isRelevantOrder)
    note_attributes: p?.deliveryDate ? [{ name: 'Delivery Date', value: String(p.deliveryDate) }] : undefined,
    packingStatus: p?.fulfillmentStatus && p.fulfillmentStatus !== 'UNFULFILLED' ? 'packed' : 'pending',
    trackingNumber: tracking,
    trackingCompany: p?.trackingCompany || p?.carrier?.title || undefined,
    // Kurier rozpoznany przez UCS — zasila odznake kuriera (courierOfOrder) na liscie/mapie.
    carrier: p?.carrier || undefined,
    // Powód z UCS. BEZ tego lista admina nie miała z czego wnioskować, że metoda wysyłki jest
    // nierozpoznana, i pokazywała fałszywą plakietkę kuriera (patrz isUnidentifiedOrder).
    reason: typeof p?.reason === 'string' ? p.reason : undefined,
  };
}

/** Zamówienie spakowane dziś (GET /api/packing/orders/packed-today) — patrz packedToday.ts w API. */
export type PackedTodayOrder = {
  id: number | null;
  order_id: string | null;
  name: string;
  station_id?: string | null;
  user_name?: string | null;
  packed_at: string;
  duration_seconds?: number | null;
  total_items?: number | null;
  packages_count: number;
  tracking_number?: string | null;
  carrier?: string | null;
  packingStatus: 'packed';
};

/**
 * Spakowane w bieżącym dniu. Lista zamówień rysuje kolejkę UNFULFILLED z UCS, więc zamówienie
 * po spakowaniu z niej wypada — bez tego odczytu zakładka „Spakowane" pokazywała 0 przy
 * kilkudziesięciu spakowanych. retry:false, bo starsze API nie ma tej trasy: wtedy po prostu
 * nie dokładamy nic.
 */
export function usePackedToday() {
  return useQuery<PackedTodayOrder[]>({
    queryKey: ['packed-today'],
    queryFn: async () => {
      const r = await axios.get(`${API_URL}/api/packing/orders/packed-today`);
      return Array.isArray(r.data) ? r.data : [];
    },
    refetchInterval: 30000,
    retry: false,
  });
}

export function useAdminOrders() {
  return useQuery<AdminOrder[]>({
    queryKey: ['orders'],
    queryFn: async () => {
      // Panel admina (mapa dostaw + licznik oczekujących + lista zamówień) pokazuje
      // WSZYSTKIE niezrealizowane zamówienia, nie tylko kolejkę „na teraz":
      // /api/ucs/packages/all = dziś + zaległe + z terminem w przyszłości + bez terminu.
      // (Stacja pakowania nadal używa /packages/now — kolejka na dziś.)
      try {
        const r = await axios.get(`${API_URL}/api/ucs/packages/all`);
        return Array.isArray(r.data?.packages) ? r.data.packages.map(mapUcsPackageToOrder) : [];
      } catch { return []; }
    },
    refetchInterval: 15000,
  });
}

/* Status agenta on-prem dla wskaźnika w shellu (desktop + mobile).
   Źródłem prawdy jest /api/agents (jak tabela w Ustawieniach), nie socket.
   „online" = istnieje aktywny agent ze świeżym heartbeatem. */
export function useAgentOnline(): boolean {
  const { data } = useQuery<any[]>({
    queryKey: ['print-agents'],
    queryFn: async () => {
      const d = (await axios.get(`${API_URL}/api/agents`)).data;
      return Array.isArray(d) ? d : d?.agents || [];
    },
    refetchInterval: 30000,
  });
  return (data || []).some(isAgentOnline);
}

/* Hub mapy — z danych firmy tenanta (konfiguracja nadawcy w Ustawieniach).
   Gdy miasto nadawcy jest nieznane/nieustawione, zostaje domyślny hub. */
export function useSenderHub(): Hub {
  // Konfiguracja nadawcy z /api/settings/sender — `city` wyznacza hub na mapie dostaw.
  // Endpoint jest opcjonalny (pusty obiekt gdy nieskonfigurowany), więc brak danych
  // spada na DEFAULT_HUB i mapa zawsze ma poprawny punkt początkowy.
  const { data } = useQuery<{ city?: string }>({
    queryKey: ['sender-config'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/sender`)).data,
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => {
    const geo = findCity(data?.city);
    return geo || DEFAULT_HUB;
  }, [data]);
}

// Deterministyczny rozrzut wokół centroidu (≈20–55 km) — stabilny per etykieta
// (hash nazwy), więc znaczniki nie skaczą między odświeżeniami, a różne miasta
// tego samego województwa rozsuwają się zamiast nakładać na jeden punkt.
function jitterLatLon(seed: string, lat: number, lon: number): [number, number] {
  const h = hash32(seed);
  const angle = ((h % 3600) / 3600) * Math.PI * 2;
  const radius = 0.18 + (((h >>> 12) % 1000) / 1000) * 0.32; // 0.18..0.50°
  return [lat + Math.sin(angle) * radius, lon + Math.cos(angle) * radius];
}

/* Lokalizacja zamówienia na mapie. Etykietą jest ZAWSZE realna nazwa miasta z
   zamówienia (gdy jest), więc na mapie widać prawdziwe miasta — także te spoza
   słownika. Pozycja:
     • miasto w słowniku → dokładne współrzędne (approx:false),
     • PL spoza słownika → centroid województwa z kodu pocztowego/province + rozrzut,
     • zagranica/PL bez kodu → centroid kraju (ostateczny fallback PL) + rozrzut.
   Żadne zamówienie nie wypada z mapy — licznik przesyłek == liczba zamówień. */
function resolveOrderGeo(o: AdminOrder): { label: string; lat: number; lon: number; approx: boolean } | null {
  const addr = o.shipping_address;
  const cc = (addr?.country_code || 'PL').trim().toUpperCase();
  const cityName = addr?.city?.trim() || undefined;

  const exact = findCity(cityName);
  if (exact) return { label: exact.label, lat: exact.lat, lon: exact.lon, approx: false };

  let base: GeoPlace | null = null;
  if (cc === 'PL') base = findVoivodeshipPlace(addr?.province, addr?.zip);
  if (!base) base = findCountry(cc) || findCountry('PL');
  if (!base) return null;

  // Realna nazwa miasta jako etykieta; pozycja przybliżona do centroidu + rozrzut.
  const label = cityName || base.label;
  const [lat, lon] = jitterLatLon(`${label}|${base.label}`, base.lat, base.lon);
  return { label, lat, lon, approx: true };
}

/* Agregacja dostaw per punkt docelowy — zasila mape. Miasto rozpoznawane po
   slowniku (PL + Europa); nieznane miasto PL spada na centroid wojewodztwa, a
   nieznane za granica na centroid kraju — wiec KAZDE zamowienie jest widoczne. */
export function useDeliveries(orders: AdminOrder[] | undefined, sessions: ActiveSession[] | undefined): Delivery[] {
  return useMemo(() => {
    if (!orders?.length) return [];
    const liveOrderNames = new Set((sessions || []).map((s) => s.order_name));
    const byPlace = new Map<string, Delivery>();
    for (const o of orders) {
      const geo = resolveOrderGeo(o);
      if (!geo) continue;
      const cur = byPlace.get(geo.label) || {
        city: geo.label, lat: geo.lat, lon: geo.lon,
        count: 0, courier: courierOfOrder(o), live: false,
      };
      cur.count += 1;
      if (liveOrderNames.has(o.name)) cur.live = true;
      byPlace.set(geo.label, cur);
    }
    return Array.from(byPlace.values());
  }, [orders, sessions]);
}

/* Śledzenie pojedynczych paczek — zasila żywą mapę. Każde zamówienie z
   rozpoznanym celem staje się paczką: pending/aktywna sesja → „kompletowanie”
   (krąży wokół huba), spakowane → „wysłana” (podróżuje trasą kuriera). Numer
   śledzenia i faza ruchu są deterministyczne (hash zamówienia), więc paczki nie
   skaczą między odświeżeniami danych, a mapa pozostaje stabilna i czytelna. */
const TRACK_PREFIX: Record<CourierKey, string> = {
  inpost: 'IP', paczkomat: 'PCZ', dhl: 'DHL', kurier: 'KUR', goodspeed: 'GS', cod: 'COD',
  // Nierozpoznana metoda dostawy — nie ma i nie będzie prawdziwego numeru u kuriera,
  // bo UCS nie wystawi dla takiej paczki etykiety. Prefiks istnieje tylko po to, żeby
  // mapa nie wywaliła się na brakującym kluczu.
  unknown: 'N/A',
};

function hash32(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Nadana przesyłka z UCS (GET /api/packing/shipments/in-transit) — patrz shipmentsInTransit.ts. */
export type ShipmentInTransit = {
  trackingNumber: string;
  status: string;
  carrier?: string | null;
  orderName?: string | null;
  shopifyOrderId?: number | null;
  city?: string | null;
  countryCode?: string | null;
  createdAt: string;
};

/**
 * Paczki NADANE (z UCS) — bez nich mapa rysowała wyłącznie kolejkę do spakowania, więc
 * „w drodze" zawsze pokazywało 0: przesyłka po nadaniu wypada z kolejki zamówień
 * niezrealizowanych. Adresy rozwiązuje API (z cache'em), tu tylko odczyt.
 */
export function useShipmentsInTransit() {
  return useQuery<ShipmentInTransit[]>({
    queryKey: ['shipments-in-transit'],
    queryFn: async () => {
      const r = await axios.get(`${API_URL}/api/packing/shipments/in-transit`);
      return Array.isArray(r.data) ? r.data : [];
    },
    refetchInterval: 30000,
    retry: false,
  });
}

/** Nadane przesyłki → znaczniki mapy. Bez rozpoznanego miasta paczki nie da się postawić. */
export function useDispatchedParcels(shipments: ShipmentInTransit[] | undefined, hub: Hub): TrackedParcel[] {
  return useMemo(() => {
    const out: TrackedParcel[] = [];
    for (const s of shipments || []) {
      if (!s.trackingNumber) continue;
      const geo = (s.city ? findCity(s.city) : null) || findCountry((s.countryCode || 'PL').toUpperCase());
      if (!geo) continue;
      out.push({
        id: `ucs-${s.trackingNumber}`,
        orderName: s.orderName || s.trackingNumber,
        city: s.city || geo.label,
        country: s.countryCode && s.countryCode !== 'PL' ? s.countryCode : undefined,
        courier: courierOfTitle(s.carrier || ''),
        lat: geo.lat,
        lon: geo.lon,
        status: 'shipped',
        distanceKm: distanceKm({ lat: hub.lat, lon: hub.lon }, { lat: geo.lat, lon: geo.lon }),
        seed: (hash32(s.trackingNumber) % 1000) / 1000,
        tracking: s.trackingNumber,
        trackingNumber: s.trackingNumber,
      });
    }
    return out;
  }, [shipments, hub]);
}

export function useTrackedParcels(
  orders: AdminOrder[] | undefined,
  sessions: ActiveSession[] | undefined,
  hub: Hub,
): TrackedParcel[] {
  return useMemo(() => {
    if (!orders?.length) return [];
    const liveOrderNames = new Set((sessions || []).map((s) => s.order_name));
    const shipped: TrackedParcel[] = [];
    const preparing: TrackedParcel[] = [];

    orders.forEach((o, i) => {
      const addr = o.shipping_address;
      const cc = (addr?.country_code || 'PL').trim().toUpperCase();
      const geo = resolveOrderGeo(o);
      if (!geo) return;
      // geo.label to już realna nazwa miasta z zamówienia (gdy jest) — widoczna w dymku/panelu.
      const cityLabel = geo.label;

      const courier = courierOfOrder(o);
      const orderName = o.name || String(o.id ?? i);
      const h = hash32(`${orderName}|${geo.label}`);
      // Numer śledzenia => paczka realnie nadana (śledzona przez UCS) — zawsze
      // „shipped”. Bez numeru: pending/aktywna sesja => „kompletowanie”.
      const hasTracking = !!o.trackingNumber;
      const isPreparing = !hasTracking && (o.packingStatus === 'pending' || liveOrderNames.has(o.name));
      const customer = [o.customer?.first_name, o.customer?.last_name].filter(Boolean).join(' ') || undefined;

      const parcel: TrackedParcel = {
        id: `${o.id ?? orderName}-${i}`,
        orderName,
        customer,
        city: cityLabel,
        zip: addr?.zip || undefined,
        country: cc !== 'PL' ? (countryName(cc) || undefined) : undefined,
        courier,
        lat: geo.lat,
        lon: geo.lon,
        status: isPreparing ? 'preparing' : 'shipped',
        distanceKm: Math.round(distanceKm(hub, geo)),
        seed: (h % 10000) / 10000,
        // Realny numer z UCS gdy jest; inaczej wewnętrzna referencja (podgląd).
        tracking: o.trackingNumber || `REF-${TRACK_PREFIX[courier] || 'PF'}-${String(h % 100000000).padStart(8, '0')}`,
        trackingNumber: o.trackingNumber || undefined,
      };
      (isPreparing ? preparing : shipped).push(parcel);
    });

    // Zwracamy WSZYSTKIE paczki — liczniki na mapie (w drodze / kompletowanie) muszą
    // być spójne z licznikiem oczekujących i pokazywać pełen stan. Limit liczby
    // JEDNOCZEŚNIE animowanych znaczników jest po stronie renderu (DeliveryMap), nie danych.
    return [...shipped, ...preparing];
  }, [orders, sessions, hub]);
}

/* Realny tracking UCS na żywo. Dla paczek z numerem śledzenia odpytuje
   GET /api/ucs/track/:trackingNumber (zdarzenia kuriera) i wzbogaca paczki o
   ostatni status + lokalizację, którymi mapa steruje fazą i pozycją znacznika.
   Gdy żadna paczka nie ma numeru (kolejka do pakowania) — useQueries dostaje
   pustą listę i NIE wykonuje żadnych żądań (zero kosztu, mapa animuje poglądowo). */
type UcsTrackEvent = {
  status: TrackStatus;
  description?: string;
  location?: { city?: string; state?: string; countryCode?: string };
  occurredAt: string;
};

export function useLiveTracking(parcels: TrackedParcel[]): TrackedParcel[] {
  const numbers = useMemo(
    () => Array.from(new Set(parcels.map((p) => p.trackingNumber).filter((x): x is string => !!x))).slice(0, 40),
    [parcels],
  );

  const results = useQueries({
    queries: numbers.map((tn) => ({
      queryKey: ['ucs-track', tn],
      queryFn: async (): Promise<{ trackingNumber?: string; events?: UcsTrackEvent[] }> =>
        (await axios.get(`${API_URL}/api/ucs/track/${encodeURIComponent(tn)}`)).data,
      refetchInterval: 60000,
      staleTime: 30000,
      retry: false,
    })),
  });

  // Ostatnie zdarzenie per numer (najnowsze occurredAt).
  const latest = useMemo(() => {
    const m: Record<string, UcsTrackEvent> = {};
    numbers.forEach((tn, i) => {
      const evs = (results[i]?.data as { events?: UcsTrackEvent[] } | undefined)?.events;
      if (evs?.length) {
        m[tn] = [...evs].sort((a, b) => +new Date(b.occurredAt) - +new Date(a.occurredAt))[0];
      }
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbers, results.map((r) => r.dataUpdatedAt).join(',')]);

  return useMemo(() => parcels.map((p) => {
    const ev = p.trackingNumber ? latest[p.trackingNumber] : undefined;
    if (!ev) return p;
    const geo = ev.location?.city ? findCity(ev.location.city) : null;
    return {
      ...p,
      status: 'shipped' as const,
      liveStatus: ev.status,
      liveDesc: ev.description,
      liveCity: geo?.label || ev.location?.city,
      liveLat: geo?.lat,
      liveLon: geo?.lon,
      liveAt: ev.occurredAt,
    };
  }), [parcels, latest]);
}

export type RankRow = { name: string; count: number; share: number };

/* Rankingi celow wysylek: miasta, regiony (wojewodztwo / prowincja) oraz kraje. */
export function useDestinationRankings(
  orders: AdminOrder[] | undefined,
  top = 7,
): { cities: RankRow[]; regions: RankRow[]; countries: RankRow[] } {
  return useMemo(() => {
    const cities = new Map<string, { name: string; count: number }>();
    const regions = new Map<string, { name: string; count: number }>();
    const countries = new Map<string, { name: string; count: number }>();
    let cityTotal = 0;
    let regionTotal = 0;
    let countryTotal = 0;

    const bump = (m: Map<string, { name: string; count: number }>, name: string) => {
      const key = name.toLowerCase();
      const cur = m.get(key) || { name, count: 0 };
      cur.count += 1;
      m.set(key, cur);
    };

    for (const o of orders || []) {
      const addr = o.shipping_address;
      const rawCity = (addr?.city || '').trim();
      const cc = (addr?.country_code || 'PL').trim().toUpperCase();

      if (rawCity) {
        const geo = findCity(rawCity);
        bump(cities, geo ? geo.label : rawCity.replace(/\s+/g, ' '));
        cityTotal += 1;
      }

      // Region = jednostka administracyjna I rzędu (woj. dla PL, prowincja dla zagranicy).
      const region = regionOf(cc, addr?.province, addr?.zip, rawCity);
      if (region) {
        bump(regions, region);
        regionTotal += 1;
      }

      // Kraj — osobny ranking (kraj nie jest regionem).
      const country = countryName(cc) || (addr?.country || '').trim() || cc;
      if (country) {
        bump(countries, country);
        countryTotal += 1;
      }
    }

    const toRows = (m: Map<string, { name: string; count: number }>, total: number): RankRow[] =>
      Array.from(m.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, top)
        .map((r) => ({ ...r, share: total ? r.count / total : 0 }));

    return {
      cities: toRows(cities, cityTotal),
      regions: toRows(regions, regionTotal),
      countries: toRows(countries, countryTotal),
    };
  }, [orders, top]);
}

export type FeedEvent = { kind: 'packed' | 'print' | 'scan' | 'error' | 'ship' | 'start'; t: string; who: string; at: number };

/* Feed "na zywo" budowany z sesji + historii pakowania. */
export function useLiveFeed(sessions: ActiveSession[] | undefined, history: HistoryRow[] | undefined): (FeedEvent & { ago: string })[] {
  return useMemo(() => {
    const ev: FeedEvent[] = [];
    for (const s of sessions || []) {
      ev.push({
        kind: s.items_packed > 0 ? 'scan' : 'start',
        t: s.items_packed > 0 ? `${s.order_name || '—'} · ${s.items_packed}/${s.total_items} pozycji` : `Sesja rozpoczęta · ${s.order_name || '—'}`,
        who: `${s.user_name || '—'} · ${s.station_name || '—'}`,
        at: new Date(s.started_at).getTime(),
      });
    }
    for (const h of (history || []).slice(0, 12)) {
      ev.push({
        kind: 'packed',
        t: `${h.order_name} spakowane`,
        who: `${h.user_name || '—'} · ${h.station_name || '—'}`,
        at: new Date(h.completed_at).getTime(),
      });
    }
    ev.sort((a, b) => (Number.isFinite(b.at) ? b.at : 0) - (Number.isFinite(a.at) ? a.at : 0));
    const now = Date.now();
    return ev.slice(0, 9).map((e) => {
      const valid = Number.isFinite(e.at) && e.at > 0;
      const min = valid ? Math.max(0, Math.round((now - e.at) / 60000)) : 0;
      const ago = !valid ? '—' : min < 1 ? 'teraz' : min < 60 ? `${min} min` : `${Math.floor(min / 60)} h`;
      return { ...e, ago };
    });
  }, [sessions, history]);
}
